import { GenericError, MissingRefreshTokenError } from '@auth0/auth0-spa-js';

const REAUTH_REQUIRED_CODES = new Set(['login_required', 'missing_refresh_token', 'invalid_grant']);
const REFRESH_TOKEN_REAUTH_MESSAGE = /refresh token/i;
const REFRESH_TOKEN_TERMINAL_MESSAGE = /(expired|invalid|missing|unknown)/i;

function isReauthRequiredCode(code: unknown): boolean {
  return typeof code === 'string' && REAUTH_REQUIRED_CODES.has(code);
}

function hasRefreshTokenTerminalMessage(err: unknown): boolean {
  const messages: unknown[] = [];
  if (err instanceof Error) messages.push(err.message);
  if (typeof err === 'object' && err !== null) {
    messages.push((err as { message?: unknown }).message);
    messages.push((err as { error_description?: unknown }).error_description);
  }
  return messages.some(
    (message) =>
      typeof message === 'string' &&
      REFRESH_TOKEN_REAUTH_MESSAGE.test(message) &&
      REFRESH_TOKEN_TERMINAL_MESSAGE.test(message),
  );
}

/// `getTokenSilently()` が「もう silent には更新できない、 ユーザーを再ログインに
/// 飛ばすしかない」 種類のエラーを投げたかどうかの純粋判定。
/// - `MissingRefreshTokenError`: refresh token がキャッシュに無い (失効・未発行・
///   localStorage クリア)。
/// - `GenericError('login_required')`: refresh token は使えず silent auth も不可。
/// - `GenericError('missing_refresh_token')`: 上記の文字列バリアント。
/// - `GenericError('invalid_grant')`: refresh token 自体が Auth0 側で失効・無効化済み。
/// それ以外 (ネットワーク断・サーバエラー等) は再ログインで解決しないので false。
///
/// **再認証要否の「判断」は features 層の責務** (FSD: shared は判断を持たず
/// イベント発行のみ)。 token 取得失敗を検知する `shared/api/client` 側はこの判定を
/// 直接持たず、 配線層 (`app/auth/bootstrap`) からこの関数を predicate として
/// 注入される。
export function isReauthRequiredError(err: unknown): boolean {
  if (err instanceof MissingRefreshTokenError) return true;
  if (err instanceof GenericError) {
    return isReauthRequiredCode(err.error) || hasRefreshTokenTerminalMessage(err);
  }
  // SDK 以外の経路で投げられた例外向けの duck-typing 保険。
  if (typeof err === 'object' && err !== null && 'error' in err) {
    const code = (err as { error?: unknown }).error;
    return isReauthRequiredCode(code) || hasRefreshTokenTerminalMessage(err);
  }
  return hasRefreshTokenTerminalMessage(err);
}
